// assets/product-print-customizer.js
import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

const MIN_SCALE = 0.2;
const MAX_SCALE = 4;
const HANDLE_RADIUS = 10;

/**
 * @typedef {object} Pointer
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {object} SideState
 * @property {HTMLImageElement | null} image
 * @property {number} x - Artwork center, 0-1 fraction of canvas width.
 * @property {number} y - Artwork center, 0-1 fraction of canvas height.
 * @property {number} scale
 * @property {'drag' | 'resize' | 'pinch' | null} mode
 * @property {Map<number, Pointer>} pointers
 * @property {{x: number, y: number, stateX: number, stateY: number, scale: number} | null} dragStart
 * @property {number | null} pinchStartDistance
 * @property {number | null} pinchStartScale
 */

/**
 * @typedef {object} ProductPrintCustomizerRefs
 * @property {HTMLButtonElement[]} tab
 * @property {HTMLElement[]} panel
 * @property {HTMLImageElement[]} [mockupImg]
 * @property {HTMLCanvasElement[]} [canvas]
 * @property {HTMLInputElement[]} fileInput
 * @property {HTMLInputElement[]} placementInput
 * @property {HTMLDialogElement} [confirmDialog]
 * @property {HTMLImageElement[]} [confirmPreviewImg]
 */

/**
 * Renders a product mockup with a draggable/resizable artwork clipped to a
 * dashed print-area guide, per side (Front/Back/Left Sleeve/Right Sleeve).
 * @extends Component<ProductPrintCustomizerRefs>
 */
class ProductPrintCustomizer extends Component {
  /** @type {Map<string, SideState>} */
  #state = new Map();
  /** @type {Map<string, ResizeObserver>} */
  #observers = new Map();
  /** @type {HTMLButtonElement | null} */
  #addToCartButton = null;
  #confirmed = false;

  /** @type {AbortController | undefined} */
  #colorSyncController;

  connectedCallback() {
    super.connectedCallback?.();
    for (const canvas of this.refs.canvas ?? []) {
      this.#initCanvas(canvas);
    }
    this.#initAddToCartIntercept();
    this.#initConfirmDialog();
    this.#initMockupDialog();
    this.#initColorSync();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    for (const observer of this.#observers.values()) {
      observer.disconnect();
    }
    this.#addToCartButton?.removeEventListener('click', this.#handleAddToCartClick, true);
    if (this.refs.confirmDialog) unlockScroll(this.refs.confirmDialog);
    if (this.refs.mockupDialog) unlockScroll(this.refs.mockupDialog);
    this.#colorSyncController?.abort();
  }

  /**
   * Listens for the theme's variant-select event (fired when the customer
   * picks a different Size/Color) so the mockup art can swap to match the
   * newly selected color without discarding any artwork already placed.
   */
  #initColorSync() {
    const target = this.closest('.shopify-section, dialog');
    if (!target) return;

    this.#colorSyncController = new AbortController();
    target.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#colorSyncController.signal,
    });
  }

  /** @param {CustomEvent & { promise?: Promise<{ detail?: { html?: Document } }> }} event */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;

        const newComponent = html.querySelector(this.tagName.toLowerCase());
        if (!newComponent) return;

        for (const newImg of newComponent.querySelectorAll('.design-canvas__mockup')) {
          if (!(newImg instanceof HTMLImageElement)) continue;
          const side = newImg.dataset.side;
          const currentImg = side ? this.#findMockupImg(side) : null;
          const newSrc = newImg.getAttribute('src');
          if (!currentImg || !newSrc || currentImg.getAttribute('src') === newSrc) continue;

          currentImg.setAttribute('src', newSrc);
          currentImg.setAttribute('width', newImg.getAttribute('width') ?? '');
          currentImg.setAttribute('height', newImg.getAttribute('height') ?? '');
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-print-customizer] Event promise rejected:', error);
      });
  };

  #initConfirmDialog() {
    const dialog = this.refs.confirmDialog;
    if (!dialog) return;

    dialog.addEventListener('close', () => unlockScroll(dialog));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) this.closeConfirmDialog();
    });
  }

  #initMockupDialog() {
    const dialog = this.refs.mockupDialog;
    if (!dialog) return;

    dialog.addEventListener('close', () => unlockScroll(dialog));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) this.closeMockupDialog();
    });
  }

  /**
   * Opens the mockup adjustment dialog, switching to the given side's tab.
   * Bindable directly via `on:click="/openMockupDialog?side=front"`.
   * @param {{ side?: string }} data
   */
  openMockupDialog(data) {
    const side = data?.side;
    const dialog = this.refs.mockupDialog;
    if (!dialog) return;

    if (side) this.#selectSide(side);
    if (!dialog.open) {
      lockScroll(dialog);
      dialog.showModal();
    }

    const canvas = side ? this.#findCanvas(side) : null;
    if (canvas) this.#resizeCanvas(canvas);
  }

  /**
   * Closes the mockup adjustment dialog.
   */
  closeMockupDialog() {
    const dialog = this.refs.mockupDialog;
    if (!dialog?.open) return;
    dialog.close();
    unlockScroll(dialog);
  }

  /** @param {string} side */
  #selectSide(side) {
    for (const tab of this.refs.tab ?? []) {
      tab.setAttribute('aria-selected', tab.dataset.side === side ? 'true' : 'false');
    }
    for (const panel of this.refs.panel ?? []) {
      panel.hidden = panel.dataset.side !== side;
    }
  }

  /**
   * Updates (and reveals) the file chip for a side, and the overall
   * "File attached" status indicator.
   * @param {string} side
   * @param {File} file
   */
  #updateFileChip(side, file) {
    const chip = (this.refs.fileChip ?? []).find((el) => el.dataset.side === side);
    const name = (this.refs.fileChipName ?? []).find((el) => el.dataset.side === side);
    const size = (this.refs.fileChipSize ?? []).find((el) => el.dataset.side === side);

    if (name) name.textContent = file.name;
    if (size) size.textContent = formatFileSize(file.size);
    if (chip) chip.hidden = false;

    if (this.refs.uploadStatus) this.refs.uploadStatus.hidden = false;
    if (this.refs.notUploadedStatus) this.refs.notUploadedStatus.hidden = true;
    if (this.refs.noFileHelp) this.refs.noFileHelp.hidden = true;
  }

  /**
   * Intercepts the product form's Add to Cart button so the customer can
   * review every side's artwork placement before the item is actually added.
   */
  #initAddToCartIntercept() {
    const formId = /** @type {HTMLElement | null} */ (this.querySelector('[data-product-form-id]'))?.dataset
      .productFormId;
    if (!formId) return;

    const form = document.getElementById(formId);
    const button = /** @type {HTMLButtonElement | null} */ (
      form?.querySelector('button[type="submit"][name="add"]')
    );
    if (!button) return;

    this.#addToCartButton = button;
    button.addEventListener('click', this.#handleAddToCartClick, true);
  }

  /** @param {MouseEvent} event */
  #handleAddToCartClick = (event) => {
    if (this.#confirmed) {
      this.#confirmed = false;
      return;
    }

    event.preventDefault();
    this.#openConfirmDialog();
  };

  #openConfirmDialog() {
    const dialog = this.refs.confirmDialog;
    if (!dialog) return;

    lockScroll(dialog);
    dialog.showModal();
    this.#renderConfirmPreviews();
  }

  /**
   * Fills in each side's preview tile once its mockup photo has actually
   * finished downloading. On slower (mobile) connections the mockup images
   * may still be in flight when the dialog opens, so rendering happens
   * per-image as each one becomes ready instead of only once, synchronously,
   * at click time.
   */
  async #renderConfirmPreviews() {
    for (const img of this.refs.confirmPreviewImg ?? []) {
      const side = img.dataset.side;
      if (!side) continue;
      const mockupImg = this.#findMockupImg(side);
      if (mockupImg) await this.#waitForImageLoad(mockupImg);
      const preview = this.#renderSidePreview(side);
      if (preview) img.src = preview;
    }
  }

  /**
   * Resolves once the image has finished loading (or errored, or 4s have
   * passed) so a slow or broken mockup photo can never block the dialog.
   * @param {HTMLImageElement} img
   */
  #waitForImageLoad(img) {
    if (img.complete && img.naturalWidth) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        resolve();
      };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      setTimeout(done, 4000);
    });
  }

  /**
   * Closes the confirmation dialog without adding the item to the cart.
   */
  closeConfirmDialog() {
    const dialog = this.refs.confirmDialog;
    if (!dialog?.open) return;
    dialog.close();
    unlockScroll(dialog);
  }

  /**
   * Re-triggers the real Add to Cart click after the customer confirms.
   */
  handleConfirmAddToCart() {
    this.closeConfirmDialog();
    if (!this.#addToCartButton) return;
    this.#confirmed = true;
    this.#addToCartButton.click();
  }

  /**
   * Renders a clean (no guide box, no drag handle) preview of a side's
   * mockup with its placed artwork, at the mockup's native resolution.
   * @param {string} side
   * @returns {string | null} A PNG data URL, or null if the mockup isn't loaded yet.
   */
  #renderSidePreview(side) {
    const img = this.#findMockupImg(side);
    if (!img || !img.naturalWidth || !img.naturalHeight) return null;

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const state = this.#state.get(side);
    if (state?.image) {
      const area = this.#getPrintArea(img);
      const rect = {
        x: ((area.x ?? 30) / 100) * canvas.width,
        y: ((area.y ?? 20) / 100) * canvas.height,
        width: ((area.width ?? 40) / 100) * canvas.width,
        height: ((area.height ?? 55) / 100) * canvas.height,
      };

      ctx.save();
      ctx.beginPath();
      ctx.rect(rect.x, rect.y, rect.width, rect.height);
      ctx.clip();

      const box = this.#getArtworkBox(canvas.width, canvas.height, state);
      ctx.drawImage(state.image, box.x, box.y, box.width, box.height);
      ctx.restore();
    }

    return canvas.toDataURL('image/png');
  }

  /** @param {HTMLCanvasElement} canvas */
  #initCanvas(canvas) {
    const side = canvas.dataset.side;
    if (!side) return;

    this.#state.set(side, {
      image: null,
      x: 0.5,
      y: 0.5,
      scale: 1,
      mode: null,
      pointers: new Map(),
      dragStart: null,
      pinchStartDistance: null,
      pinchStartScale: null,
    });

    const img = this.#findMockupImg(side);
    if (img) {
      const resize = () => this.#resizeCanvas(canvas);
      if (img.complete) resize();
      img.addEventListener('load', resize);

      const observer = new ResizeObserver(resize);
      observer.observe(img);
      this.#observers.set(side, observer);
    }

    canvas.addEventListener('pointerdown', (event) => this.#onPointerDown(event, canvas));
    canvas.addEventListener('pointermove', (event) => this.#onPointerMove(event, canvas));
    canvas.addEventListener('pointerup', (event) => this.#onPointerUp(event, canvas));
    canvas.addEventListener('pointercancel', (event) => this.#onPointerUp(event, canvas));
  }

  /** @param {string} side */
  #findMockupImg(side) {
    return (this.refs.mockupImg ?? []).find((img) => img.dataset.side === side) ?? null;
  }

  /** @param {string} side */
  #findCanvas(side) {
    return (this.refs.canvas ?? []).find((canvas) => canvas.dataset.side === side) ?? null;
  }

  /** @param {Element} element - The canvas or mockup img inside a `.design-canvas-wrapper`. */
  #getPrintArea(element) {
    const wrapper = element.closest('.design-canvas-wrapper');
    const raw = wrapper?.getAttribute('data-print-area');
    if (!raw) return { x: 30, y: 20, width: 40, height: 55 };
    try {
      return JSON.parse(raw);
    } catch {
      return { x: 30, y: 20, width: 40, height: 55 };
    }
  }

  /** @param {HTMLCanvasElement} canvas */
  #resizeCanvas(canvas) {
    const side = canvas.dataset.side;
    const img = side ? this.#findMockupImg(side) : null;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    canvas.width = rect.width;
    canvas.height = rect.height;
    this.#draw(canvas);
  }

  /** @param {Event} event */
  handleFileChange(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const side = input.dataset.side;
    const file = input.files?.[0];
    if (!side || !file || !file.type.startsWith('image/')) return;

    const state = this.#state.get(side);
    if (!state) return;

    this.#updateFileChip(side, file);

    const img = new Image();
    img.onload = () => {
      state.image = img;
      state.x = 0.5;
      state.y = 0.5;
      state.scale = 1;
      const canvas = this.#findCanvas(side);
      if (canvas) this.#draw(canvas);
      this.#emitPlacement(side);
      this.openMockupDialog({ side });
    };
    img.src = URL.createObjectURL(file);
  }

  /** @param {Event} event */
  handleTabClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const side = target.dataset.side;
    if (!side) return;

    this.#selectSide(side);

    const canvas = this.#findCanvas(side);
    if (canvas) this.#resizeCanvas(canvas);
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLCanvasElement} canvas
   */
  #onPointerDown(event, canvas) {
    const side = canvas.dataset.side;
    const state = side ? this.#state.get(side) : null;
    if (!state?.image) return;

    canvas.setPointerCapture(event.pointerId);
    const point = this.#eventPoint(event, canvas);
    state.pointers.set(event.pointerId, point);

    if (state.pointers.size === 1) {
      const handle = this.#getHandlePosition(canvas, state);
      const distanceToHandle = Math.hypot(point.x - handle.x, point.y - handle.y);
      state.mode = distanceToHandle <= HANDLE_RADIUS * 2 ? 'resize' : 'drag';
      state.dragStart = { x: point.x, y: point.y, stateX: state.x, stateY: state.y, scale: state.scale };
    } else if (state.pointers.size === 2) {
      state.mode = 'pinch';
      state.pinchStartDistance = this.#pointerDistance(state.pointers);
      state.pinchStartScale = state.scale;
    }
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLCanvasElement} canvas
   */
  #onPointerMove(event, canvas) {
    const side = canvas.dataset.side;
    const state = side ? this.#state.get(side) : null;
    if (!state?.image || !state.pointers.has(event.pointerId)) return;

    const point = this.#eventPoint(event, canvas);
    state.pointers.set(event.pointerId, point);

    if (state.mode === 'drag' && state.pointers.size === 1 && state.dragStart) {
      state.x = state.dragStart.stateX + (point.x - state.dragStart.x) / canvas.width;
      state.y = state.dragStart.stateY + (point.y - state.dragStart.y) / canvas.height;
    } else if (state.mode === 'resize' && state.pointers.size === 1 && state.dragStart) {
      const delta = (point.x - state.dragStart.x) / (canvas.width * 0.3);
      state.scale = clamp(state.dragStart.scale + delta, MIN_SCALE, MAX_SCALE);
    } else if (state.mode === 'pinch' && state.pointers.size === 2 && state.pinchStartDistance) {
      const distance = this.#pointerDistance(state.pointers);
      state.scale = clamp((state.pinchStartScale ?? 1) * (distance / state.pinchStartDistance), MIN_SCALE, MAX_SCALE);
    } else {
      return;
    }

    this.#draw(canvas);
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLCanvasElement} canvas
   */
  #onPointerUp(event, canvas) {
    const side = canvas.dataset.side;
    const state = side ? this.#state.get(side) : null;
    if (!state) return;

    state.pointers.delete(event.pointerId);
    if (state.pointers.size === 0) {
      state.mode = null;
      state.dragStart = null;
      if (side) this.#emitPlacement(side);
    }
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLCanvasElement} canvas
   */
  #eventPoint(event, canvas) {
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  /** @param {Map<number, Pointer>} pointers */
  #pointerDistance(pointers) {
    const [a, b] = [...pointers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {SideState} state
   */
  #getHandlePosition(canvas, state) {
    const box = this.#getArtworkBox(canvas.width, canvas.height, state);
    return { x: box.x + box.width, y: box.y + box.height };
  }

  /**
   * @param {number} width
   * @param {number} height
   * @param {SideState} state
   */
  #getArtworkBox(width, height, state) {
    const baseSize = Math.min(width, height) * 0.5;
    const aspect = (state.image?.naturalWidth || 1) / (state.image?.naturalHeight || 1);
    const boxWidth = baseSize * state.scale;
    const boxHeight = boxWidth / aspect;
    const centerX = state.x * width;
    const centerY = state.y * height;
    return { x: centerX - boxWidth / 2, y: centerY - boxHeight / 2, width: boxWidth, height: boxHeight };
  }

  /** @param {HTMLCanvasElement} canvas */
  #draw(canvas) {
    const side = canvas.dataset.side;
    const state = side ? this.#state.get(side) : null;
    const ctx = canvas.getContext('2d');
    if (!state || !ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const area = this.#getPrintArea(canvas);
    const rect = {
      x: ((area.x ?? 30) / 100) * canvas.width,
      y: ((area.y ?? 20) / 100) * canvas.height,
      width: ((area.width ?? 40) / 100) * canvas.width,
      height: ((area.height ?? 55) / 100) * canvas.height,
    };

    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    ctx.lineDashOffset = 6;
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.9)';
    ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    ctx.restore();

    if (!state.image) return;

    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.width, rect.height);
    ctx.clip();

    const box = this.#getArtworkBox(canvas.width, canvas.height, state);
    ctx.drawImage(state.image, box.x, box.y, box.width, box.height);
    ctx.restore();

    const handle = this.#getHandlePosition(canvas, state);
    ctx.save();
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#1e5fd2';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(handle.x, handle.y, HANDLE_RADIUS, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  /** @param {string} side */
  #emitPlacement(side) {
    const state = this.#state.get(side);
    const input = (this.refs.placementInput ?? []).find((el) => el.dataset.side === side);
    if (!state || !input) return;
    input.value = JSON.stringify({ x: round(state.x), y: round(state.y), scale: round(state.scale) });
  }
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/** @param {number} n */
function round(n) {
  return Math.round(n * 1000) / 1000;
}

/** @param {number} bytes */
function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

customElements.define('product-print-customizer-component', ProductPrintCustomizer);
